import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import mesApi, {
  LabosNalog,
  LabosScaledRow,
  LabosTrialOption,
  LabosUredaj,
} from "../../../Api/mes.api";
import { Ingredient } from "../../../types/mes";
import NumberInput from "../../../components/DataEntry/NumberInput";
import LabosShell from "./components/LabosShell";
import {
  formatLabosDate,
  NALOG_STATUS_LABEL,
  TARGET_BRASNO_G,
  UREDAJ_LABEL,
} from "./labosUtils";
import { t } from "../../../i18n/locale";

type PageTab = "plan" | "kreiraj";
type CreateMode = "trial" | "scratch";

interface ScratchRow {
  ingredient_name: string;
  ingredient_type: string;
  baker_percentage: number;
}

const LabosNalozi = () => {
  const [pageTab, setPageTab] = useState<PageTab>("plan");
  const [nalozi, setNalozi] = useState<LabosNalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterUredaj, setFilterUredaj] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDays, setFilterDays] = useState(30);

  const [trials, setTrials] = useState<LabosTrialOption[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  const [uredaj, setUredaj] = useState<LabosUredaj>("farinograf");
  const [plannedDate, setPlannedDate] = useState(new Date().toISOString().split("T")[0]);
  const [operator, setOperator] = useState("");
  const [notes, setNotes] = useState("");
  const [createMode, setCreateMode] = useState<CreateMode>("trial");
  const [trialId, setTrialId] = useState<number | "">("");
  const [sampleName, setSampleName] = useState("");
  const [scaledPreview, setScaledPreview] = useState<LabosScaledRow[]>([]);
  const [totalMass, setTotalMass] = useState(0);
  const [scaleMeta, setScaleMeta] = useState<{
    water_default_ml?: number;
    starter_water_g?: number;
    water_to_add_g?: number;
    salt_g?: number;
    water_by_method?: boolean;
  }>({});
  const [scratchRows, setScratchRows] = useState<ScratchRow[]>([
    { ingredient_name: "", ingredient_type: "", baker_percentage: 0 },
  ]);
  const [saving, setSaving] = useState(false);

  const loadNalozi = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mesApi.getLabosNalozi({
        days: filterDays,
        status: filterStatus || undefined,
        uredaj: filterUredaj || undefined,
      });
      setNalozi(data);
    } catch (err: any) {
      toast.error(err.message);
      setNalozi([]);
    } finally {
      setLoading(false);
    }
  }, [filterDays, filterStatus, filterUredaj]);

  useEffect(() => {
    if (pageTab === "plan") loadNalozi();
  }, [pageTab, loadNalozi]);

  useEffect(() => {
    mesApi.getLabosTrialsSelect().then(setTrials).catch(() => {});
    mesApi.getIngredients().then(setIngredients).catch(() => {});
  }, []);

  const applyScale = (data: {
    scaled: LabosScaledRow[];
    total_mass_g: number;
    water_default_ml?: number;
    starter_water_g?: number;
    water_to_add_g?: number;
    salt_g?: number;
    water_by_method?: boolean;
  }) => {
    setScaledPreview(data.scaled);
    setTotalMass(data.total_mass_g);
    setScaleMeta({
      water_default_ml: data.water_default_ml,
      starter_water_g: data.starter_water_g,
      water_to_add_g: data.water_to_add_g,
      salt_g: data.salt_g,
      water_by_method: data.water_by_method,
    });
  };

  const clearScale = () => {
    setScaledPreview([]);
    setTotalMass(0);
    setScaleMeta({});
  };

  const runScaleFromTrial = async (tid: number, u: LabosUredaj) => {
    const data = await mesApi.scaleLabosFromTrial(tid, u);
    applyScale(data);
  };

  useEffect(() => {
    if (pageTab !== "kreiraj" || createMode !== "trial" || !trialId) {
      if (createMode === "trial" && !trialId) clearScale();
      return;
    }
    const t = trials.find((x) => x.trial_id === Number(trialId));
    if (t) setSampleName(`${t.trial_code} — ${t.product_name || ""}`);
    runScaleFromTrial(Number(trialId), uredaj).catch((e) => toast.error(e.message));
  }, [trialId, uredaj, createMode, pageTab]);

  const runScratchScale = async (rows: ScratchRow[]) => {
    const valid = rows.filter((r) => r.ingredient_name && r.baker_percentage > 0);
    if (!valid.length) {
      clearScale();
      return;
    }
    const data = await mesApi.previewLabosScale(uredaj, valid);
    applyScale(data);
  };

  // Re-skaliraj scratch recepturu kada se promijeni uređaj.
  useEffect(() => {
    if (pageTab === "kreiraj" && createMode === "scratch") {
      runScratchScale(scratchRows).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uredaj]);

  const updateScratch = (idx: number, field: keyof ScratchRow, value: string | number) => {
    const next = [...scratchRows];
    const row = { ...next[idx], [field]: value };
    if (field === "ingredient_name" && typeof value === "string") {
      const ing = ingredients.find((i) => i.IngredientName === value);
      if (ing) row.ingredient_type = ing.IngredientTypeName || "";
    }
    next[idx] = row;
    setScratchRows(next);
    runScratchScale(next).catch(() => {});
  };

  const handleCreate = async () => {
    if (!scaledPreview.length) {
      toast.error(t("Nema skaliranih sastojaka"));
      return;
    }
    const sn = sampleName.trim();
    if (!sn) {
      toast.error(t("Unesite naziv uzorka"));
      return;
    }
    setSaving(true);
    try {
      const created = await mesApi.createLabosNalog({
        uredaj,
        planned_date: plannedDate,
        sample_name: sn,
        operator: operator || undefined,
        notes: notes || undefined,
        trial_id: createMode === "trial" && trialId ? Number(trialId) : null,
        ingredients: scaledPreview,
      });
      toast.success(t("Nalog {code} kreiran", { code: created.nalog_code }));
      setPageTab("plan");
      setScaledPreview([]);
      setScratchRows([{ ingredient_name: "", ingredient_type: "", baker_percentage: 0 }]);
      loadNalozi();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (nalogId: number, status: LabosNalog["status"]) => {
    try {
      await mesApi.updateLabosNalogStatus(nalogId, status);
      toast.success(t("Status ažuriran"));
      loadNalozi();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const ingNames = ["", ...ingredients.map((i) => i.IngredientName)];

  return (
    <LabosShell title={t("MES - Labos nalozi")}>
      <ToastContainer position="top-center" autoClose={2500} />

      <div className="mes-section">
        <div className="kpi-tabs fq-analysis-tabs">
          <button
            type="button"
            className={`kpi-tab ${pageTab === "plan" ? "active" : ""}`}
            onClick={() => setPageTab("plan")}
          >
            {t("Plan naloga")}
          </button>
          <button
            type="button"
            className={`kpi-tab ${pageTab === "kreiraj" ? "active" : ""}`}
            onClick={() => setPageTab("kreiraj")}
          >
            {t("Kreiraj nalog")}
          </button>
        </div>
      </div>

      {pageTab === "plan" && (
        <>
          <div className="mes-section">
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div className="control-group">
                <label>{t("Uređaj")}</label>
                <select className="select-classic" value={filterUredaj} onChange={(e) => setFilterUredaj(e.target.value)}>
                  <option value="">{t("Svi")}</option>
                  {(Object.keys(UREDAJ_LABEL) as LabosUredaj[]).map((u) => (
                    <option key={u} value={u}>
                      {t(UREDAJ_LABEL[u])}
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label>{t("Status")}</label>
                <select className="select-classic" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">{t("Svi")}</option>
                  {(Object.keys(NALOG_STATUS_LABEL) as LabosNalog["status"][]).map((s) => (
                    <option key={s} value={s}>
                      {t(NALOG_STATUS_LABEL[s])}
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label>{t("Razdoblje")}</label>
                <select className="select-classic" value={filterDays} onChange={(e) => setFilterDays(Number(e.target.value))}>
                  {[14, 30, 60, 90].map((d) => (
                    <option key={d} value={d}>
                      {t("Zadnjih {d} dana", { d })}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn-secondary btn-sm" onClick={loadNalozi} disabled={loading}>
                Osvježi
              </button>
            </div>
          </div>

          <div className="mes-section">
            {loading && <p>{t("Učitavanje…")}</p>}
            {!loading && nalozi.length === 0 && <p className="mes-hint">{t("Nema naloga za filter.")}</p>}
            {nalozi.map((n) => (
              <div key={n.nalog_id} className="labos-nalog-card">
                <div className="labos-nalog-head">
                  <strong>{t(NALOG_STATUS_LABEL[n.status])}</strong>
                  <span>{t(UREDAJ_LABEL[n.uredaj])}</span>
                  <span>{formatLabosDate(n.planned_date)}</span>
                  <span>{n.nalog_code}</span>
                  <span>{n.sample_name || "—"}</span>
                </div>
                <div className="labos-nalog-body">
                  <p>
                    {t("Target brašno:")} <strong>{n.target_brasno_g} g</strong>
                    {n.operator && <> · {t("Operator")}: {n.operator}</>}
                    {n.trial_code && (
                      <>
                        {" "}
                        · {t("Proba")}: <strong>{n.trial_code}</strong> {n.trial_product}
                      </>
                    )}
                  </p>
                  {n.notes && <p className="mes-hint">{n.notes}</p>}
                  {n.ingredients && n.ingredients.length > 0 && (
                    <div className="mes-table-wrap">
                      <table className="mes-table mes-table-compact">
                      <thead>
                        <tr>
                          <th>{t("Sirovina")}</th>
                          <th>{t("Tip")}</th>
                          <th>{t("Masa (g)")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.ingredients.map((ing, i) => (
                          <tr key={i}>
                            <td>{ing.ingredient_name}</td>
                            <td>{ing.ingredient_type || "—"}</td>
                            <td>{ing.mass_g}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {n.status === "plan" && (
                      <>
                        <button type="button" className="btn-sm btn-primary" onClick={() => setStatus(n.nalog_id, "u_tijeku")}>
                          {t("Pokreni")}
                        </button>
                        <button type="button" className="btn-sm btn-danger" onClick={() => setStatus(n.nalog_id, "otkazano")}>
                          {t("Otkaži")}
                        </button>
                      </>
                    )}
                    {n.status === "u_tijeku" && (
                      <>
                        <button type="button" className="btn-sm btn-success" onClick={() => setStatus(n.nalog_id, "zavrseno")}>
                          {t("Završi")}
                        </button>
                        <button type="button" className="btn-sm btn-danger" onClick={() => setStatus(n.nalog_id, "otkazano")}>
                          {t("Otkaži")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pageTab === "kreiraj" && (
        <div className="mes-section">
          <div className="form-grid-2">
            <div className="control-group">
              <label>{t("Uređaj")}</label>
              <select
                className="select-classic"
                value={uredaj}
                onChange={(e) => setUredaj(e.target.value as LabosUredaj)}
              >
                {(Object.keys(UREDAJ_LABEL) as LabosUredaj[]).map((u) => (
                  <option key={u} value={u}>
                    {t(UREDAJ_LABEL[u])}
                  </option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>{t("Datum mjerenja")}</label>
              <input type="date" className="input-classic" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
            </div>
            <div className="control-group">
              <label>{t("Operator")}</label>
              <input className="input-classic" value={operator} onChange={(e) => setOperator(e.target.value)} />
            </div>
          </div>
          <p className="mes-hint">{t("Target masa brašna: {value} g", { value: TARGET_BRASNO_G[uredaj] })}</p>
          {uredaj === "amilograf" && (
            <p className="mes-hint">
              {t("Uključuju se samo brašno-sastojci (uklj. brašno iz startera i poluproizvoda). Voda je fiksno {ml} mL, umanjeno za vodu iz startera.", { ml: scaleMeta.water_default_ml ?? 450 })}
            </p>
          )}
          {uredaj === "ekstenzograf" && (
            <p className="mes-hint">{t("Dodaje se 2% soli na masu brašna ({grams} g).", { grams: (TARGET_BRASNO_G[uredaj] * 2) / 100 })}</p>
          )}
          {uredaj === "farinograf" && (
            <p className="mes-hint">{t("QC uzorci brašna imaju prioritet. Količina vode ovisi o odabranoj metodi mjerenja.")}</p>
          )}

          <div style={{ margin: "1rem 0" }}>
            <label style={{ marginRight: 12 }}>
              <input
                type="radio"
                checked={createMode === "trial"}
                onChange={() => setCreateMode("trial")}
              />{" "}
              {t("Iz probe")}
            </label>
            <label>
              <input
                type="radio"
                checked={createMode === "scratch"}
                onChange={() => {
                  setCreateMode("scratch");
                  clearScale();
                }}
              />{" "}
              {t("Iz nule")}
            </label>
          </div>

          {createMode === "trial" && (
            <div className="control-group" style={{ maxWidth: 480 }}>
              <label>{t("Proba")}</label>
              <select
                className="select-classic"
                value={trialId}
                onChange={(e) => setTrialId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">{t("— odaberi —")}</option>
                {trials.map((trial) => (
                  <option key={trial.trial_id} value={trial.trial_id}>
                    {trial.trial_code} — {formatLabosDate(trial.trial_date)} — {trial.product_name || "—"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {createMode === "scratch" && (
            <>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>{t("Sirovina")}</th>
                    <th>{t("Tip")}</th>
                    <th>{t("PEK %")}</th>
                  </tr>
                </thead>
                <tbody>
                  {scratchRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <select
                          className="select-classic"
                          value={row.ingredient_name}
                          onChange={(e) => updateScratch(idx, "ingredient_name", e.target.value)}
                        >
                          {ingNames.map((name) => (
                            <option key={name || "empty"} value={name}>
                              {name || t("— odaberi —")}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{row.ingredient_type || "—"}</td>
                      <td>
                        <NumberInput
                          className="input-classic"
                          step="0.1"
                          value={row.baker_percentage}
                          onValueChange={(v) => updateScratch(idx, "baker_percentage", v)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setScratchRows([
                      ...scratchRows,
                      { ingredient_name: "", ingredient_type: "", baker_percentage: 0 },
                    ])
                  }
                >
                  {t("+ Sirovina")}
                </button>
                {scratchRows.length > 1 && (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setScratchRows(scratchRows.slice(0, -1))}>
                    {t("Ukloni zadnju")}
                  </button>
                )}
              </div>
              <div className="control-group" style={{ marginTop: 12, maxWidth: 400 }}>
                <label>{t("Naziv uzorka")}</label>
                <input className="input-classic" value={sampleName} onChange={(e) => setSampleName(e.target.value)} />
              </div>
            </>
          )}

          {scaledPreview.length > 0 && (
            <>
              <h4 style={{ marginTop: "1.5rem" }}>{t("Receptura skalirana ({grams} g brašna)", { grams: TARGET_BRASNO_G[uredaj] })}</h4>
              <div className="mes-table-wrap">
                <table className="mes-table mes-table-compact">
                <thead>
                  <tr>
                    <th>{t("Sirovina")}</th>
                    <th>{t("Tip")}</th>
                    <th>{t("Masa (g)")}</th>
                  </tr>
                </thead>
                <tbody>
                  {scaledPreview.map((r, i) => (
                    <tr key={i} style={r.is_manual_water ? { fontWeight: 600 } : undefined}>
                      <td>
                        {r.ingredient_name}
                        {r.is_manual_water ? " 💧" : ""}
                      </td>
                      <td>{r.ingredient_type || "—"}</td>
                      <td>{r.mass_g}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="mes-hint">{t("Ukupna masa zamjesa: {value} g", { value: totalMass.toFixed(1) })}</p>
              {uredaj === "amilograf" && scaleMeta.water_to_add_g != null && (
                <p className="mes-hint">
                  {t("Voda: {ml} mL − {starter} g (starter) =", { ml: scaleMeta.water_default_ml, starter: scaleMeta.starter_water_g ?? 0 })}{" "}
                  <strong>{t("{ml} mL za ručno dodati", { ml: scaleMeta.water_to_add_g })}</strong>
                </p>
              )}
              {uredaj === "ekstenzograf" && scaleMeta.salt_g != null && (
                <p className="mes-hint">{t("Dodana sol:")} <strong>{scaleMeta.salt_g} g</strong> {t("(2% brašna)")}</p>
              )}
              {uredaj === "farinograf" && scaleMeta.water_by_method && (
                <p className="mes-hint">{t("Voda se dodaje prema metodi mjerenja (definira operater na uređaju).")}</p>
              )}
            </>
          )}

          <div className="control-group" style={{ marginTop: 12 }}>
            <label>{t("Napomena")}</label>
            <input className="input-classic" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <button type="button" className="btn-primary" style={{ marginTop: 16 }} onClick={handleCreate} disabled={saving}>
            {saving ? t("Spremanje…") : t("Kreiraj nalog")}
          </button>
        </div>
      )}
    </LabosShell>
  );
};

export default LabosNalozi;
